import { getService } from 'react-in-angularjs';
import { AngularEventKeys } from '../../../constants/system/ANGULAR_EVENT_KEYS';

/**
 * Broadcasts AngularJS event and triggers digest cycle to update HTML templates.
 * Used to notify AngularJS controllers when React code changes shared state.
 *
 * @param {AngularEventKeys} eventKey - Event key from ANGULAR_EVENT_KEYS
 *
 * @example
 * // React: Broadcast event
 * import { broadcastEvent } from '../utils/angularUtils';
 * import { ANGULAR_EVENT_KEYS } from '../constants/system/ANGULAR_EVENT_KEYS';
 *
 * handleSolutionChange(solution: SolutionType) {
 *   localStorage.setItem(ACTIVE_SOLUTION, solution);
 *   broadcastEvent(ANGULAR_EVENT_KEYS.ACTIVE_SOLUTION_CHANGED);
 * }
 *
 * @example
 * // Option 1: Using functions in HTML (no $scope.$on needed)
 * // HTML: {{vm.getCoachLabel()}}
 * // Controller:
 * vm.getCoachLabel = () => utilService.getUserRoleLabel(AGENT);
 * // utilService.applyAsyncFunc(); trigerr digest cycle to update HTML templates
 * // Function re-executes on every digest → reads latest value automatically
 *
 * @example
 * // Option 2: Using properties in HTML (need $scope.$on to update)
 * // HTML: <div ng-if="vm.activeSolutionFlags.NUTRITION">
 * // Controller:
 * $scope.$on('ACTIVE_SOLUTION_CHANGED', function() {
 *   vm.activeSolutionFlags = utilService.getActiveSolutionFlags();
 * });
 * // Properties are static → must update manually
 *
 * @remarks
 * - Functions in {{ }} re-execute on digest → no $scope.$on needed
 * - Properties in ng-* need $scope.$on to update → then template re-evaluates
 * - Digest cycle re-evaluates all {{ }} expressions and ng-* directives
 */
export const broadcastEvent = (eventKey: AngularEventKeys) => {
  const $rootScope = getService('$rootScope');
  const utilService = getService('utilService');

  $rootScope.$broadcast(eventKey);
  utilService.applyAsyncFunc();
};
